import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Code2, Copy, Receipt, Puzzle, ExternalLink, Eye, Sparkles, Users, Timer } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../lib/site";

/* /embed — public gallery for partner sites: showcases TravelerPulse's embeddable
   widgets and generates iframe snippets. First widget: shared trip-expense summary. */

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function EmbedGallery() {
  return (
    <div data-testid="embed-gallery">
      <section className="bg-indigo-950 text-white border-b border-zinc-200">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 py-14">
          <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-3 flex items-center gap-2">
            <Puzzle className="w-4 h-4" /> Embeddable widgets — {SITE.name || "TravelerPulse"}
          </div>
          <h1 className="font-heading font-900 text-4xl sm:text-5xl tracking-tight" data-testid="embed-gallery-title">
            Put TravelerPulse on your site
          </h1>
          <p className="text-white/80 text-lg mt-3 max-w-2xl">
            Free, no-signup iframe widgets for blogs, family sites and partner platforms.
            Paste one line of HTML — the widget stays live and read-only.
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-5 lg:px-8 py-12 space-y-8">
        <ExpenseWidgetCard />
        <CountdownWidgetCard />

        {/* coming soon strip */}
        <div className="border border-dashed border-zinc-300 p-5" data-testid="embed-coming-soon">
          <div className="font-heading font-800 text-sm uppercase tracking-wide text-muted-foreground mb-3">More widgets coming</div>
          <div className="grid sm:grid-cols-2 gap-3 text-sm text-zinc-600">
            <div className="flex items-start gap-2"><Users className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" /> Group poll — let blog readers vote on your itinerary choices</div>
            <div className="flex items-start gap-2"><Sparkles className="w-4 h-4 text-violet-600 mt-0.5 shrink-0" /> Destination weather strip — live conditions for your next stop</div>
          </div>
          <p className="text-xs text-muted-foreground mt-3">
            Want a specific widget for your platform? <Link to="/complaints" className="text-primary hover:underline">Tell us</Link> — white-label options are on the roadmap.
          </p>
        </div>
      </section>
    </div>
  );
}

function ExpenseWidgetCard() {
  const [input, setInput] = useState("");
  const [height, setHeight] = useState(640);
  const [status, setStatus] = useState(null); // null | "checking" | "live" | "dead"

  // Accept a full share URL or a bare token
  const token = useMemo(() => {
    const raw = input.trim();
    if (!raw) return "";
    const m = raw.match(/trip-expenses\/shared\/([A-Za-z0-9_-]+)/);
    if (m) return m[1];
    return /^[A-Za-z0-9_-]{10,}$/.test(raw) ? raw : "";
  }, [input]);

  // Validate the token against the live API (check=1 doesn't inflate view counts)
  useEffect(() => {
    if (!token) { setStatus(null); return; }
    let stale = false;
    setStatus("checking");
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/expenses/shared/${token}?check=1`)
      .then(() => !stale && setStatus("live"))
      .catch(() => !stale && setStatus("dead"));
    return () => { stale = true; };
  }, [token]);

  const url = token ? `${ORIGIN}/trip-expenses/shared/${token}` : "";
  const iframe = token
    ? `<iframe src="${url}?embed=1" width="100%" height="${height}" style="border:1px solid #d4d4d8" title="Trip expense summary"></iframe>`
    : "";
  const copy = () => { navigator.clipboard?.writeText(iframe); toast.success("Embed code copied"); };

  return (
    <div className="border border-zinc-300 bg-white" data-testid="embed-expense-widget">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
        <span className="inline-flex p-2.5 border border-emerald-200 bg-emerald-50 text-emerald-700"><Receipt className="w-6 h-6" /></span>
        <div>
          <h2 className="font-heading font-900 text-xl">Trip Expense Summary</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            A live, read-only card with the trip's totals, category breakdown and who-owes-whom settlement.
            Never shows receipts, notes or documents. The owner can revoke it anytime.
          </p>
        </div>
      </div>
      <div className="p-5 grid lg:grid-cols-2 gap-6">
        <div>
          <ol className="text-sm text-zinc-700 space-y-1.5 mb-4 list-decimal list-inside">
            <li>Open your trip → <b>My Trip Expenses</b> → <b>Share summary</b> → Create share link</li>
            <li>Paste the link (or its token) below</li>
            <li>Copy the embed code into any website's HTML</li>
          </ol>
          <label className="text-xs font-mono uppercase text-muted-foreground">Share link or token</label>
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="embed-token-input"
            placeholder={`${ORIGIN}/trip-expenses/shared/…`}
            className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-emerald-600" />
          <div className="flex items-center gap-2 mt-3">
            <label className="text-xs font-mono uppercase text-muted-foreground">Height</label>
            <input type="number" min={320} max={1200} step={20} value={height}
              onChange={(e) => setHeight(Number(e.target.value) || 640)} data-testid="embed-height-input"
              className="w-24 border border-zinc-300 px-2 py-1 text-sm" />
            <span className="text-xs text-muted-foreground">px</span>
          </div>
          {token && status === "checking" && (
            <p className="text-xs text-muted-foreground mt-4" data-testid="embed-token-checking">Checking the link…</p>
          )}
          {token && status === "dead" && (
            <p className="text-xs text-rose-600 mt-4" data-testid="embed-token-dead">
              This share link isn't active — the owner may have turned it off. Copy a fresh one from the Share summary panel.
            </p>
          )}
          {token && status === "live" ? (
            <div className="mt-4 space-y-2">
              <code className="block bg-zinc-50 border border-zinc-300 px-3 py-2 text-[11px] break-all" data-testid="embed-snippet">{iframe}</code>
              <div className="flex flex-wrap gap-2">
                <button onClick={copy} data-testid="embed-copy-btn"
                  className="inline-flex items-center gap-1.5 bg-emerald-700 text-white px-4 py-2 text-sm font-700 hover:bg-emerald-600">
                  <Copy className="w-4 h-4" /> Copy embed code
                </button>
                <a href={`${url}?embed=1`} target="_blank" rel="noreferrer" data-testid="embed-open-link"
                  className="inline-flex items-center gap-1.5 border border-zinc-300 px-4 py-2 text-sm font-700 text-zinc-700 hover:border-emerald-600">
                  <ExternalLink className="w-4 h-4" /> Open widget
                </a>
              </div>
            </div>
          ) : input.trim() && !token ? (
            <p className="text-xs text-rose-600 mt-3" data-testid="embed-token-invalid">That doesn't look like a share link or token — copy it from the Share summary panel.</p>
          ) : null}
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-muted-foreground mb-1.5 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live preview</div>
          {token && status === "live" ? (
            <iframe src={`${url}?embed=1`} title="Trip expense summary preview" data-testid="embed-preview"
              width="100%" height={Math.min(height, 560)} style={{ border: "1px solid #d4d4d8" }} />
          ) : (
            <div className="border border-dashed border-zinc-300 bg-zinc-50 h-64 flex items-center justify-center text-sm text-muted-foreground" data-testid="embed-preview-empty">
              <Code2 className="w-4 h-4 mr-2" /> Paste a share link to preview the widget here
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* Widget 2: Trip Countdown — token from the trip's Sharing & Visibility panel. */
function CountdownWidgetCard() {
  const [input, setInput] = useState("");
  const [status, setStatus] = useState(null);

  const token = useMemo(() => {
    const raw = input.trim();
    if (!raw) return "";
    const m = raw.match(/widgets\/countdown\/([A-Za-z0-9_-]+)/);
    if (m) return m[1];
    return /^[A-Za-z0-9_-]{8,}$/.test(raw) ? raw : "";
  }, [input]);

  useEffect(() => {
    if (!token) { setStatus(null); return; }
    let stale = false;
    setStatus("checking");
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/embed/countdown/${token}?check=1`)
      .then(() => !stale && setStatus("live"))
      .catch(() => !stale && setStatus("dead"));
    return () => { stale = true; };
  }, [token]);

  const url = token ? `${ORIGIN}/widgets/countdown/${token}` : "";
  const iframe = token
    ? `<iframe src="${url}?embed=1" width="360" height="340" style="border:0" title="Trip countdown"></iframe>`
    : "";

  return (
    <div className="border border-zinc-300 bg-white" data-testid="embed-countdown-widget">
      <div className="px-5 py-4 border-b border-zinc-200 flex items-start gap-3">
        <span className="inline-flex p-2.5 border border-indigo-200 bg-indigo-50 text-indigo-700"><Timer className="w-6 h-6" /></span>
        <div>
          <h2 className="font-heading font-900 text-xl">Trip Countdown</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            A live "12 days to Kerala" card — perfect for blogs and family sites. Shows only the trip
            title, dates and stops; switches to "Day N of M" during the trip.
          </p>
        </div>
      </div>
      <div className="p-5 grid lg:grid-cols-2 gap-6">
        <div>
          <ol className="text-sm text-zinc-700 space-y-1.5 mb-4 list-decimal list-inside">
            <li>Open your trip → <b>Sharing &amp; Visibility</b> → <b>Trip countdown widget</b> → Create countdown link</li>
            <li>Paste the link (or token) below and copy the embed code</li>
          </ol>
          <label className="text-xs font-mono uppercase text-muted-foreground">Countdown link or token</label>
          <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="embed-countdown-input"
            placeholder={`${ORIGIN}/widgets/countdown/…`}
            className="w-full border border-zinc-300 px-3 py-2 text-sm mt-1 focus:outline-none focus:border-indigo-600" />
          {token && status === "checking" && <p className="text-xs text-muted-foreground mt-3">Checking the link…</p>}
          {token && status === "dead" && (
            <p className="text-xs text-rose-600 mt-3" data-testid="embed-countdown-dead">This countdown isn't active — the owner may have turned it off.</p>
          )}
          {token && status === "live" && (
            <div className="mt-4 space-y-2">
              <code className="block bg-zinc-50 border border-zinc-300 px-3 py-2 text-[11px] break-all" data-testid="embed-countdown-snippet">{iframe}</code>
              <button onClick={() => { navigator.clipboard?.writeText(iframe); toast.success("Embed code copied"); }} data-testid="embed-countdown-copy"
                className="inline-flex items-center gap-1.5 bg-indigo-700 text-white px-4 py-2 text-sm font-700 hover:bg-indigo-600">
                <Copy className="w-4 h-4" /> Copy embed code
              </button>
            </div>
          )}
          {input.trim() && !token && <p className="text-xs text-rose-600 mt-3">That doesn't look like a countdown link or token.</p>}
        </div>
        <div>
          <div className="text-xs font-mono uppercase text-muted-foreground mb-1.5 flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" /> Live preview</div>
          {token && status === "live" ? (
            <iframe src={`${url}?embed=1`} title="Trip countdown preview" data-testid="embed-countdown-preview"
              width="360" height="340" style={{ border: "1px solid #d4d4d8" }} />
          ) : (
            <div className="border border-dashed border-zinc-300 bg-zinc-50 h-64 flex items-center justify-center text-sm text-muted-foreground">
              <Code2 className="w-4 h-4 mr-2" /> Paste a countdown link to preview it here
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
